/**
 * AppLayout
 *
 * Topbar: Branch/Office Switcher, theme toggle, user badge.
 * Sidebar: "WIP System" branding (logo from /gt.ico, always visible even
 * when collapsed -- only the text label hides) + Dashboard, Scheduling &
 * Timesheet, Engagement Summary, Engagement Progressing, Staff
 * Utilization Report, User Permissions, Settings. Collapsible (persisted
 * to localStorage) so wide pages (e.g. Engagement Progressing's
 * 32-column table) have more room.
 *
 * `min-w-0` on the flex-column wrapper and `<main>` below is deliberate,
 * not decorative -- without it, a flex item's default `min-width: auto`
 * means it won't shrink below its content's intrinsic width, so a wide
 * table inside `<main>` was forcing the WHOLE LAYOUT (sidebar included)
 * to grow and the entire page body to scroll horizontally together,
 * rather than just the table's own internal scroll container. `min-w-0`
 * lets `<main>` fill only the remaining space next to the sidebar and
 * clip/scroll its own overflow internally.
 */
import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import {
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Gauge,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Settings,
  ShieldCheck,
  Users,
} from 'lucide-react';
import { useBranchContext } from '../hooks/useBranchContext.js';
import { useAuth } from '../hooks/useAuth.js';
import { ThemeToggle } from '../components/ThemeToggle.jsx';

const NAV_ITEMS = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/variance-matrix', label: 'Scheduling & Timesheet', icon: BarChart3 },
  { to: '/engagement-summary', label: 'Engagement Summary', icon: Gauge },
  { to: '/engagement-progressing', label: 'Engagement Progressing', icon: ListChecks },
  { to: '/staff-utilization', label: 'Staff Utilization Report', icon: Users },
  { to: '/user-permissions', label: 'User Permissions', icon: ShieldCheck },
  { to: '/settings', label: 'Settings', icon: Settings },
];

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'sidebar_collapsed';

export const AppLayout = () => {
  const { branches, activeBranchId, setActiveBranchId } = useBranchContext();
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true'
  );

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(next));
      return next;
    });
  };

  return (
    <div className="flex min-h-screen bg-base text-primary">
      <aside
        className={`relative shrink-0 border-r border-edge bg-surface/60 p-4 transition-[width] duration-150 ${
          collapsed ? 'w-16' : 'w-64'
        }`}
      >
        <button
          type="button"
          onClick={toggleCollapsed}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="absolute -right-3 top-6 flex h-6 w-6 items-center justify-center rounded-full border border-edge bg-surface text-muted shadow-sm hover:text-primary"
        >
          {collapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronLeft className="h-3.5 w-3.5" />}
        </button>

        <div className={`mb-6 flex items-center gap-2 ${collapsed ? 'justify-center' : 'px-2'}`}>
          <img
            src="/gt.ico"
            alt="WIP System"
            className="h-6 w-6 shrink-0 rounded object-contain"
          />
          {!collapsed && (
            <p className="truncate text-sm font-semibold tracking-wide text-primary">
              WIP System
            </p>
          )}
        </div>

        <nav className="flex flex-col gap-1">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              title={collapsed ? item.label : undefined}
              className={({ isActive }) =>
                `flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors ${
                  collapsed ? 'justify-center' : ''
                } ${
                  isActive
                    ? 'bg-accent/20 text-accent'
                    : 'text-muted hover:bg-surface-hover hover:text-primary'
                }`
              }
            >
              <item.icon className="h-4 w-4 shrink-0" />
              {!collapsed && <span className="truncate">{item.label}</span>}
            </NavLink>
          ))}
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between border-b border-edge bg-surface/40 px-6 py-3">
          <div className="flex items-center gap-3">
            <select
              value={activeBranchId ?? ''}
              onChange={(e) => setActiveBranchId(e.target.value || null)}
              className="rounded-md border border-edge bg-base px-3 py-1.5 text-sm text-primary focus:border-accent focus:outline-none"
            >
              <option value="">All Branches</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-3">
            <ThemeToggle />

            {user && (
              <div className="flex items-center gap-2 rounded-md border border-edge py-1 pl-1 pr-3">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-accent/20 text-xs font-semibold text-accent">
                  {user.displayName?.charAt(0)?.toUpperCase() ?? '?'}
                </span>
                <div className="leading-tight">
                  <p className="text-xs font-medium text-primary">{user.displayName}</p>
                  <p className="text-[10px] text-muted">{user.position ?? user.role ?? '\u2014'}</p>
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={logout}
              className="flex items-center gap-1.5 rounded-md border border-edge px-3 py-1.5 text-sm text-secondary hover:bg-surface-hover"
            >
              <LogOut className="h-3.5 w-3.5" />
              Sign out
            </button>
          </div>
        </header>

        <main className="min-w-0 flex-1 overflow-x-hidden overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
